// src/components/GameRules.js
//
// アリーナ・ガチャのルールをメンバーに公開するための画面。
//
// 数値は settings/config（appConfig）から読むため、管理画面で報酬や確率を
// 変更しても説明が古くならない。未設定の項目は Cloud Functions 側の
// 既定値と同じ値をここでフォールバックとして持つ。
//
// 使い方（ボタンとモーダルが一体になっているので1行置くだけ）:
//   <GameRulesButton appConfig={appConfig} />
//
import React, { useState } from 'react';

// Cloud Functions 側の既定値と必ず揃えること
const DEF = {
    arenaEntryFee: 50,
    arenaWinReward: 100,
    arenaDrawReward: 50,
    arenaMaxPaid: 5,
    arenaTypeBonus: 1.15,
    trainingCost: 50,
    trainingSuperRate: 5,
    trainingGreatRate: 25,
    gachaTypes: {
        normal:   { cost: 50,   pulls: 1 },
        special:  { cost: 200,  pulls: 1 },
        special5: { cost: 1000, pulls: 5 }
    },
    gachaProbs: {
        normal:   { N: 65, R: 35, S: 5,  SR: 0,  SSR: 0, UR: 0,   LEGEND: 0 },
        special:  { N: 0,  R: 60, S: 30, SR: 8,  SSR: 2, UR: 0,   LEGEND: 0 },
        special5: { N: 0,  R: 40, S: 38, SR: 16, SSR: 5, UR: 0.9, LEGEND: 0.1 }
    },
    gachaRarities: {
        N:      { multiplier: 1.0,  minStat: 20 },
        R:      { multiplier: 1.15, minStat: 23 },
        S:      { multiplier: 1.35, minStat: 27 },
        SR:     { multiplier: 1.6,  minStat: 32 },
        SSR:    { multiplier: 2.0,  minStat: 40 },
        UR:     { multiplier: 2.6,  minStat: 52 },
        LEGEND: { multiplier: 3.5,  minStat: 70 }
    },
    cardSellPrices: { N: 10, R: 30, S: 50, SR: 100, SSR: 300, UR: 600, LEGEND: 1500 }
};

const RARITY_ORDER = ['N', 'R', 'S', 'SR', 'SSR', 'UR', 'LEGEND'];

// src/utils.js の MY_CARD_TIERS と揃えること
const MY_TIERS = [
    { min: 0,    max: 199,  id: 'MY',        name: 'マイカード',        icon: '⭐',   multiplier: 1.0 },
    { min: 200,  max: 399,  id: 'MY_PLUS',   name: 'マイカード+',       icon: '⭐⭐',  multiplier: 1.3 },
    { min: 400,  max: 699,  id: 'MY_PP',     name: 'マイカード++',      icon: '⭐⭐⭐', multiplier: 1.7 },
    { min: 700,  max: 1199, id: 'MY_ACE',    name: 'マイカード ACE',    icon: '👑',   multiplier: 2.2 },
    { min: 1200, max: null, id: 'MY_LEGEND', name: 'マイカード LEGEND', icon: '🔥',   multiplier: 2.8 }
];

const RARITY_COLOR = {
    N: '#cd7f32', R: '#9e9e9e', S: '#1976d2', SR: '#f9a825',
    SSR: '#e91e63', UR: '#6d4c41', LEGEND: '#546e7a'
};

const TYPES = [
    { icon: '⚔️', name: '攻撃型', basis: 'OFF + SPD', beats: '技術型' },
    { icon: '🎯', name: '技術型', basis: 'TEC + STA', beats: '守備型' },
    { icon: '🛡️', name: '守備型', basis: 'DEF × 2',  beats: '攻撃型' }
];

const APTITUDE = [
    { cond: 'カードのポジションと枠が完全一致', mul: '×1.0', note: 'CF の選手を CF の枠へ', color: '#2e7d32' },
    { cond: '大分類が同じ（FW / MF / DF）',     mul: '×0.8', note: 'CF の選手を ST や WG の枠へ', color: '#ef6c00' },
    { cond: 'ポジション未設定の選手',           mul: '×0.8', note: 'GK 以外の枠に置いた場合', color: '#ef6c00' },
    { cond: '大分類が違う',                     mul: '×0.5', note: 'CF の選手を CB の枠へ', color: '#c62828' },
    { cond: 'GK が絡む入れ替え',                 mul: '×0.1', note: 'GK を他の枠へ／他の選手を GK へ', color: '#c62828' }
];

const num = (v, fallback) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
};

const Section = ({ title, children, note }) => (
    <div style={{ marginBottom: '22px' }}>
        <h3 style={{
            margin: '0 0 8px 0', fontSize: '15px', color: '#1a1a1a',
            borderLeft: '4px solid #1976d2', paddingLeft: '8px'
        }}>{title}</h3>
        {note && <p style={{ fontSize: '11px', color: '#777', margin: '0 0 8px 0', lineHeight: '1.6' }}>{note}</p>}
        {children}
    </div>
);

const Table = ({ head, rows }) => (
    <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
                <tr style={{ background: '#f5f5f5' }}>
                    {head.map((h, i) => (
                        <th key={i} style={{
                            padding: '6px 8px', textAlign: i === 0 ? 'left' : 'right',
                            borderBottom: '2px solid #e0e0e0', color: '#555', fontWeight: 'bold',
                            whiteSpace: 'nowrap', background: '#f5f5f5'
                        }}>{h}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        {r.map((c, j) => (
                            <td key={j} style={{
                                padding: '6px 8px', textAlign: j === 0 ? 'left' : 'right',
                                whiteSpace: 'nowrap',
                                // ★ 色を必ず明示する。
                                //   App.css 側で color が白になっていると、
                                //   白背景の上で文字が見えなくなる。
                                color: '#222', background: 'transparent'
                            }}>{c}</td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

export const GameRulesModal = ({ appConfig = {}, onClose }) => {
    const cfg = appConfig || {};

    const entryFee   = num(cfg.arenaEntryFee,   DEF.arenaEntryFee);
    const winReward  = num(cfg.arenaWinReward,  DEF.arenaWinReward);
    const drawReward = num(cfg.arenaDrawReward, DEF.arenaDrawReward);
    const maxPaid    = num(cfg.arenaMaxPaid,    DEF.arenaMaxPaid);
    const typeBonus  = num(cfg.arenaTypeBonus,  DEF.arenaTypeBonus);
    const trCost     = num(cfg.trainingCost,      DEF.trainingCost);
    const trSuper    = num(cfg.trainingSuperRate, DEF.trainingSuperRate);
    const trGreat    = num(cfg.trainingGreatRate, DEF.trainingGreatRate);

    const gt = { ...DEF.gachaTypes, ...(cfg.gachaTypes || {}) };
    const gp = { ...DEF.gachaProbs, ...(cfg.gachaProbs || {}) };
    const sell = { ...DEF.cardSellPrices, ...(cfg.cardSellPrices || {}) };
    const rar = {};
    RARITY_ORDER.forEach(k => {
        rar[k] = { ...DEF.gachaRarities[k], ...((cfg.gachaRarities || {})[k] || {}) };
    });

    const cost = (key) => num(gt[key]?.cost, DEF.gachaTypes[key].cost);
    const prob = (key, rid) => {
        const v = (gp[key] || {})[rid];
        return Number.isFinite(Number(v)) ? Number(v) : 0;
    };
    // 表示するレアリティは、いずれかのガチャで排出されるものだけに絞る
    const shownRarities = RARITY_ORDER.filter(rid =>
        ['normal', 'special', 'special5'].some(k => prob(k, rid) > 0)
    );

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
                zIndex: 9999, display: 'flex', alignItems: 'flex-start',
                justifyContent: 'center', padding: '20px 10px', overflowY: 'auto'
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: 'white', borderRadius: '12px', maxWidth: '540px',
                    width: '100%', padding: '20px', boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                    // ★ モーダル内の基本色を固定する（親のスタイルを継承しない）
                    color: '#222', textAlign: 'left'
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h2 style={{ margin: 0, fontSize: '18px' }}>📖 カードバトルのルール</h2>
                    <button
                        onClick={onClose}
                        style={{
                            border: 'none', background: '#eee', width: '32px', height: '32px',
                            borderRadius: '50%', cursor: 'pointer', fontSize: '16px', lineHeight: 1
                        }}
                    >✕</button>
                </div>

                <Section
                    title="⚔️ チームタイプと相性"
                    note="スタメンの能力配分から自動でタイプが決まります。三すくみになっていて、有利な側は試合中の能力が上がります。"
                >
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: '6px', fontSize: '12px', fontWeight: 'bold',
                        background: '#e3f2fd', padding: '10px', borderRadius: '8px', marginBottom: '10px',
                        flexWrap: 'wrap', color: '#0d47a1'
                    }}>
                        <span>⚔️ 攻撃型</span><span style={{ color: '#1976d2' }}>▶</span>
                        <span>🎯 技術型</span><span style={{ color: '#1976d2' }}>▶</span>
                        <span>🛡️ 守備型</span><span style={{ color: '#1976d2' }}>▶</span>
                        <span>⚔️ 攻撃型</span>
                    </div>
                    <Table
                        head={['タイプ', '判定に使う能力', '有利な相手']}
                        rows={TYPES.map(t => [`${t.icon} ${t.name}`, t.basis, t.beats])}
                    />
                    <p style={{ fontSize: '11px', color: '#555', marginTop: '8px', lineHeight: '1.6' }}>
                        相性で有利なチームは、試合中の全能力が <b>×{typeBonus}</b> になります。<br />
                        攻撃力だけに振っても、守備型の相手には不利になります。バランスと読み合いが大切です。
                    </p>
                </Section>

                <Section
                    title="🎽 ポジション適性"
                    note="カードのポジションと、置いた枠が合っているほど能力を発揮します。カードに ▼ や ▼▼ が付いているのは、この補正がかかっているサインです。"
                >
                    <Table
                        head={['条件', '倍率', '例']}
                        rows={APTITUDE.map(a => [
                            a.cond,
                            <span style={{ color: a.color, fontWeight: 'bold' }}>{a.mul}</span>,
                            <span style={{ color: '#888', fontSize: '11px' }}>{a.note}</span>
                        ])}
                    />
                </Section>

                <Section
                    title="🏟️ アリーナ"
                    note="1日1回は無料で挑戦できます。2回目以降はコインを消費します。"
                >
                    <Table
                        head={['項目', '値']}
                        rows={[
                            ['参加費（2回目以降）', `${entryFee.toLocaleString()} RB`],
                            ['勝利したときの報酬', `${winReward.toLocaleString()} RB`],
                            ['引き分けたときの報酬', `${drawReward.toLocaleString()} RB`],
                            ['1日に挑戦できる回数', `無料1回 ＋ 有償${maxPaid}回`]
                        ]}
                    />
                    <p style={{ fontSize: '11px', color: '#555', marginTop: '8px', lineHeight: '1.6' }}>
                        挑戦する側にはわずかな不利補正があります。得点が決まるかどうかは、
                        両チームの能力・タイプ相性・運によって決まります。<br />
                        <span style={{ color: '#999' }}>
                            ※ 得点判定の細かい計算式は、編成が一通りに偏らないようにするため非公開です。
                        </span>
                    </p>
                </Section>

                <Section
                    title="⭐ レアリティと強さ"
                    note="カードの能力は「その選手が特訓で伸ばした能力 × レアリティ倍率」で決まります。同じレアリティでも、育てている選手のカードほど強くなります。"
                >
                    <Table
                        head={['レア', '能力倍率', '最低保証', '移籍金']}
                        rows={RARITY_ORDER.map(rid => [
                            <span style={{ color: RARITY_COLOR[rid], fontWeight: 'bold' }}>{rid}</span>,
                            `×${rar[rid].multiplier}`,
                            `${rar[rid].minStat}／項目`,
                            `${num(sell[rid], 0).toLocaleString()} RB`
                        ])}
                    />
                    <p style={{ fontSize: '11px', color: '#555', marginTop: '8px', lineHeight: '1.6' }}>
                        「最低保証」は、まだ能力を振り分けていない選手のカードが弱すぎないようにするための下限です。<br />
                        <b>UR と LEGEND は、この世に1枚しか存在しない特別なカードです。</b>
                        誰かが引いた時点で二度と排出されません。
                    </p>
                </Section>

                <Section
                    title="🎴 ガチャの費用と排出率"
                    note="1日1回、通常ガチャを無料で引けます。UR・LEGEND は5連レアガチャからのみ排出されます。"
                >
                    <Table
                        head={['種類', '費用', '枚数']}
                        rows={[
                            ['通常ガチャ', `${cost('normal').toLocaleString()} RB`, '1枚'],
                            ['レアガチャ', `${cost('special').toLocaleString()} RB`, '1枚'],
                            ['5連レアガチャ', `${cost('special5').toLocaleString()} RB`, '5枚']
                        ]}
                    />
                    <div style={{ marginTop: '12px' }}>
                        <Table
                            head={['レア', '通常', 'レア', '5連']}
                            rows={shownRarities.map(rid => [
                                <span style={{ color: RARITY_COLOR[rid], fontWeight: 'bold' }}>{rid}</span>,
                                `${prob('normal', rid)}%`,
                                `${prob('special', rid)}%`,
                                `${prob('special5', rid)}%`
                            ])}
                        />
                    </div>
                    <p style={{ fontSize: '11px', color: '#555', marginTop: '8px', lineHeight: '1.6' }}>
                        SR 以上は、対象となる会員ランクを持つメンバーから優先的に排出されます。<br />
                        引退したメンバーや、ポジションも能力も未設定のメンバーは排出されません。
                    </p>
                </Section>

                <Section
                    title="🌟 マイカードの成長"
                    note="自分自身のカード（マイカード）は、特訓で振り分けた能力の合計に応じてランクが上がります。ガチャで自分のカードは引けませんが、育てれば他人のどのカードより強くなります。"
                >
                    <Table
                        head={['振り分け合計', 'ランク', '能力倍率']}
                        rows={MY_TIERS.map(t => [
                            t.min === 0 ? '0 〜 199' : (t.max ? `${t.min} 〜 ${t.max}` : `${t.min} 以上`),
                            `${t.icon} ${t.name}`,
                            `×${t.multiplier}`
                        ])}
                    />
                    <p style={{ fontSize: '11px', color: '#555', marginTop: '8px', lineHeight: '1.6' }}>
                        他人のカードの最高は SSR の <b>×2.0</b> です。<br />
                        マイカードは鍛え続けると <b>×2.8</b> まで伸びるので、
                        <b>チームで一番強いカードは「自分を育てたカード」</b>になります。<br />
                        あわせて、自分のカードを持っている他のメンバーのカードも同時に強くなります。
                    </p>
                </Section>

                <Section
                    title="🏋️ 特訓"
                    note="能力ポイントを獲得して、自分のカードを強くできます。1日1回は無料です。"
                >
                    <Table
                        head={['結果', '獲得ポイント', '確率']}
                        rows={[
                            ['奇跡の超成功', '+5 P', `${trSuper}%`],
                            ['大成功', '+3 P', `${trGreat}%`],
                            ['成功', '+1 P', `${Math.max(0, 100 - trSuper - trGreat)}%`]
                        ]}
                    />
                    <p style={{ fontSize: '11px', color: '#555', marginTop: '8px', lineHeight: '1.6' }}>
                        2回目以降は <b>{trCost.toLocaleString()} RB</b> を消費します。<br />
                        獲得したポイントはマイページで振り分けてください。
                        <b>振り分けると、自分のカードを持っている人全員のカードも強くなります。</b>
                    </p>
                </Section>

                <div style={{
                    fontSize: '11px', color: '#777', background: '#fafafa',
                    padding: '12px', borderRadius: '8px', border: '1px solid #eee', lineHeight: '1.7'
                }}>
                    ここに書かれている数値は、チームの状況に応じて調整することがあります。<br />
                    変更した場合はこの画面にも自動で反映されます。
                </div>

                <button
                    onClick={onClose}
                    style={{
                        width: '100%', marginTop: '16px', padding: '12px', border: 'none',
                        borderRadius: '8px', background: '#1976d2', color: 'white',
                        fontWeight: 'bold', fontSize: '14px', cursor: 'pointer'
                    }}
                >閉じる</button>
            </div>
        </div>
    );
};

// ボタンとモーダルを一体にしたもの。置きたい場所に1行入れるだけで使える。
export const GameRulesButton = ({ appConfig = {}, style = {}, label = '📖 ルールを見る' }) => {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button
                onClick={() => setOpen(true)}
                style={{
                    background: 'rgba(255,255,255,0.15)', color: 'white',
                    border: '1px solid rgba(255,255,255,0.5)', borderRadius: '20px',
                    padding: '6px 14px', fontSize: '12px', fontWeight: 'bold',
                    cursor: 'pointer', ...style
                }}
            >{label}</button>
            {open && <GameRulesModal appConfig={appConfig} onClose={() => setOpen(false)} />}
        </>
    );
};

export default GameRulesButton;